import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { leadsService, projectsService, paymentsService, invoicesService } from '../../services/supabase';
import { useInfinitePayments } from '../../hooks/useInfinitePayments';
import { usePaymentRevenueData } from '../../hooks/usePaymentRevenueData';
import { PAYMENTS_DUE_STATUS_UPDATED, notifyPaymentMutated } from '../../hooks/usePaymentDueStatusSync';
import { useTableRowNavigation } from '../../hooks/useTableRowNavigation';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import { useToast } from '../../context/ToastContext';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { profilesService } from '../../services/supabase';
import { Payment, Project, Invoice } from '../../types';
import PaymentModal from '../../components/payments/PaymentModal';
import MarkAsPaidModal from '../../components/payments/MarkAsPaidModal';
import { ENABLE_INVOICES } from '../../config/featureFlags';
import { useCreateLeadFromSearch } from '../../hooks/useCreateLeadFromSearch';
import { useCreateProjectFromSearch } from '../../hooks/useCreateProjectFromSearch';
import { upsertById } from '../../utils/upsertById';
import PaymentRevenueChart from '../../components/charts/PaymentRevenueChart';
import Button from '../../components/ui/Button';
import TableSection from '../../components/ui/TableSection';
import TableFilters, { ActiveFilter, FilterFieldDef } from '../../components/ui/TableFilters';
import { matchFilterValue } from '../../utils/filterUtils';
import Card, { CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import TableCardContent from '../../components/ui/TableCardContent';
import PageHeader from '../../components/ui/PageHeader';
import { PAGE_STACK, PAGE_GRID_GAP } from '../../styles/pageLayout';
import { clickableLinks } from '../../styles/colors';
import PaymentTable from '../../components/payments/PaymentTable';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import SegmentedToggle from '../../components/ui/SegmentedToggle';
import { useBulkTableActions } from '../../hooks/useBulkTableActions';
import { useTableInitialLoading } from '../../hooks/useTableInitialLoading';
import { useSlidingWindow } from '../../hooks/useSlidingWindow';
import SlidingWindowNav from '../../components/dashboard/SlidingWindowNav';
import {
  DASHBOARD_WINDOW_SIZE,
} from '../../components/dashboard/dashboardWindowLayout';
import Tooltip from '../../components/ui/Tooltip';
import { exportToCSV, formatDateTimeForExport } from '../../utils/exportUtils';
import {
  capturePaymentCreateDeepLink,
  clearPaymentCreateDeepLink,
  peekPaymentCreateDeepLink,
} from '../../utils/paymentCreateDeepLink';
import { isOptimisticId } from '../../utils/optimisticId';
import { getRevenueAmount, revenueAmountForMarkAsPaid } from '../../utils/paymentRevenue';
import {
  downloadPaymentInvoicePdf,
  sellerFromProfile,
} from '../../utils/invoiceDownload';
import { InvoiceSellerProfile } from '../../types/invoiceSeller';
import {
  Calendar,
  Euro,
  CheckCircle,
  Plus,
} from 'lucide-react';
import Skeleton from '../../components/ui/Skeleton';

/** Single source of truth for payment filter matching (all operators). Used by table and export. */
function paymentMatchesActiveFilters(
  payment: Payment,
  project: Project | undefined,
  lead: { id: string; name?: string } | undefined,
  activeFilters: ActiveFilter[],
  paymentFilterFields: FilterFieldDef[]
): boolean {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  return activeFilters.every((f) => {
    const vRaw = (f.value ?? '').trim();
    if (f.fieldId === 'period') {
      if (!vRaw || f.operator !== 'is') return true;
      const paymentDate =
        payment.status === 'paid' && payment.paymentDate
          ? new Date(payment.paymentDate)
          : new Date(payment.dueDate);
      if (vRaw === 'month') return paymentDate.getMonth() === currentMonth && paymentDate.getFullYear() === currentYear;
      if (vRaw === 'year') return paymentDate.getFullYear() === currentYear;
      return true;
    }
    const fieldDef = paymentFilterFields.find((x) => x.id === f.fieldId);
    if (!fieldDef) return true;
    const projectName = (project?.name ?? '').toLowerCase();
    const getCellValue = (): string | number | null | undefined => {
      if (f.fieldId === 'project') return projectName;
      if (f.fieldId === 'status') return payment.status ?? '';
      if (f.fieldId === 'amount') return payment.amount ?? null;
      const dateVal = (d: string | Date | null | undefined) =>
        d != null ? (typeof d === 'string' ? d : new Date(d).toISOString().slice(0, 10)) : null;
      if (f.fieldId === 'issueDate') return dateVal(payment.issueDate);
      if (f.fieldId === 'dueDate') return dateVal(payment.dueDate);
      if (f.fieldId === 'paymentDate') return dateVal(payment.paymentDate);
      return '';
    };
    return matchFilterValue(fieldDef.type, f.operator, f.value ?? '', getCellValue());
  });
}

const Finances: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { formatDateLocale, formatCurrency, formatDateShort } = useTranslations();
  const { showToast, showToastWithUndo } = useToast();
  const { requestCreateLead, leadCreateModal } = useCreateLeadFromSearch();

  const handleCreateLeadFromSearch = useCallback(
    (args: Parameters<typeof requestCreateLead>[0]) => {
      requestCreateLead({
        prefillName: args.prefillName,
        onCreated: (lead) => {
          setLeads((prev) => upsertById(prev, lead));
          args.onCreated(lead);
        },
      });
    },
    [requestCreateLead]
  );

  const { requestCreateProject, projectCreateModal } = useCreateProjectFromSearch({
    onCreateLead: handleCreateLeadFromSearch,
  });

  const handleCreateProjectFromSearch = useCallback(
    (args: Parameters<typeof requestCreateProject>[0]) => {
      requestCreateProject({
        prefillName: args.prefillName,
        onCreated: (project) => {
          args.onCreated(project);
          setProjects((prev) => upsertById(prev, project));
          if (!project.leadId) return;
          void leadsService
            .getById(project.leadId)
            .then((lead) => {
              if (lead) setLeads((prev) => upsertById(prev, lead));
            })
            .catch(() => undefined);
        },
      });
    },
    [requestCreateProject]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    payments, 
    loadingInitial: loading, 
    error, 
    hasMore,
    loadingMore,
    loadMore,
    refresh,
    crud: paymentsCRUD,  // Optimistic CRUD operations
    addPayment: addPaginatedPayment  // Low-level for undo
  } = useInfinitePayments({
    limit: 30,
    onUpdateFailed: () => showToast(t('common.saveFailedRollback'), 'error'),
  });

  const [chartPayments, setChartPayments] = useState<Payment[]>([]);
  const [chartPaymentsLoaded, setChartPaymentsLoaded] = useState(false);
  
  // Monthly revenue - depends on selected month
  const [summaryMonth, setSummaryMonth] = useState<'this' | 'last'>('this');
  const [monthlyRevenueFromAPI, setMonthlyRevenueFromAPI] = useState<number | null>(null);
  const [summaryRevenueLoading, setSummaryRevenueLoading] = useState(true);
  
  // Calculate monthly revenue optimistically from local payments (instant feedback - standard SaaS pattern)
  // Use year/month equality (same logic as graph) to avoid timezone mismatch on dates like 31st
  const monthlyRevenueOptimistic = useMemo(() => {
    const now = new Date();
    const targetMonth = summaryMonth === 'last' ? now.getMonth() - 1 : now.getMonth();
    const targetYear = targetMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
    const adjustedMonth = targetMonth < 0 ? targetMonth + 12 : targetMonth;

    return payments
      .filter(p => {
        if (p.status !== 'paid' || !p.paymentDate) return false;
        const d = new Date(p.paymentDate);
        return d.getFullYear() === targetYear && d.getMonth() === adjustedMonth;
      })
      .reduce((sum, p) => {
        // For base currency payments, amountBaseCurrency equals amount (from transformPaymentFromDB)
        // For foreign currency payments, amountBaseCurrency contains the converted amount
        // Always use amountBaseCurrency if available, otherwise fallback to amount
        return sum + getRevenueAmount(p);
      }, 0);
  }, [payments, summaryMonth]);
  
  // Use optimistic value immediately when payments change (e.g. mark as paid)
  // Use API value when stable (more accurate for paginated lists with many payments)
  const monthlyRevenue = monthlyRevenueFromAPI !== null ? monthlyRevenueFromAPI : monthlyRevenueOptimistic;
  
  // Load from API when month changes, and sync in background when payments change
  useEffect(() => {
    let cancelled = false;
    setSummaryRevenueLoading(true);
    setMonthlyRevenueFromAPI(null);

    const now = new Date();
    const refDate = summaryMonth === 'last'
      ? new Date(now.getFullYear(), now.getMonth() - 1, 1)
      : new Date(now.getFullYear(), now.getMonth(), 1);

    paymentsService.getMonthlyRevenue(refDate.getFullYear(), refDate.getMonth())
      .then((revenue) => {
        if (!cancelled) {
          setMonthlyRevenueFromAPI(revenue);
          setSummaryRevenueLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setSummaryRevenueLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [summaryMonth]);
  
  // Sync with API in background when payments change (debounced to avoid too many calls)
  useEffect(() => {
    let cancelled = false;
    const timeoutId = setTimeout(() => {
      const now = new Date();
      const refDate = summaryMonth === 'last'
        ? new Date(now.getFullYear(), now.getMonth() - 1, 1)
        : new Date(now.getFullYear(), now.getMonth(), 1);

      paymentsService.getMonthlyRevenue(refDate.getFullYear(), refDate.getMonth())
        .then((revenue) => {
          if (!cancelled) {
            setMonthlyRevenueFromAPI(revenue);
            setSummaryRevenueLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) setSummaryRevenueLoading(false);
        });
    }, 300); // 300ms debounce (faster refresh after mark as paid)

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [payments, summaryMonth]);
  
  // Upcoming payments (sent/overdue), sorted by due date — windowed like dashboard cards
  const upcomingPayments = useMemo(() => {
    return payments
      .filter(p => p.status === 'sent' || p.status === 'overdue')
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  }, [payments]);

  const {
    visible: visibleUpcomingPayments,
    canPrev: canPrevUpcoming,
    canNext: canNextUpcoming,
    prev: prevUpcoming,
    next: nextUpcoming,
    showNav: showUpcomingNav,
  } = useSlidingWindow(upcomingPayments, DASHBOARD_WINDOW_SIZE);

  // Get payment revenue data for the chart
  const { 
    data: revenueData, 
    loading: revenueLoading,
  } = usePaymentRevenueData({ monthsToShow: 12, payments: chartPayments });

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isMarkAsPaidModalOpen, setIsMarkAsPaidModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [markingAsPaidPayment, setMarkingAsPaidPayment] = useState<Payment | null>(null);
  const [preselectedProject, setPreselectedProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [invoicesByPaymentId, setInvoicesByPaymentId] = useState<Record<string, Invoice>>({});
  const [issuerProfile, setIssuerProfile] = useState<InvoiceSellerProfile>({});

  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const statusFilter = (activeFilters.find((f) => f.fieldId === 'status' && f.operator === 'is')?.value ?? '') as 'sent' | 'paid' | 'overdue' | '';

  // Projects / Dashboard → /app/finances?createPayment=true[&projectId=…]
  useEffect(() => {
    if (searchParams.get('createPayment') === 'true') {
      const projectId = searchParams.get('projectId');
      capturePaymentCreateDeepLink(
        projectId && !isOptimisticId(projectId) ? projectId : null
      );
      const next = new URLSearchParams(searchParams);
      next.delete('createPayment');
      next.delete('projectId');
      setSearchParams(next, { replace: true });
    }

    const pending = peekPaymentCreateDeepLink();
    if (!pending) return;

    const clearTimer = window.setTimeout(() => {
      clearPaymentCreateDeepLink();
    }, 0);

    setEditingPayment(null);
    setPreselectedProject(null);
    setIsPaymentModalOpen(true);

    const projectId = pending.projectId;
    if (projectId) {
      void projectsService.getById(projectId).then((project) => {
        if (!project) return;
        setPreselectedProject(project);
        setProjects((prev) => upsertById(prev, project));
      });
    }

    return () => window.clearTimeout(clearTimer);
  }, [searchParams, setSearchParams]);

  // Payment status options for bulk update
  const paymentStatusOptions = [
    { value: 'sent', label: t('payments.statusLabels.sent') },
    { value: 'paid', label: t('payments.statusLabels.paid') },
    { value: 'overdue', label: t('payments.statusLabels.overdue') }
  ];

  const setStatusFilterFromNav = React.useCallback((value: 'sent' | 'paid' | 'overdue' | '') => {
    setActiveFilters((prev) => {
      const rest = prev.filter((f) => f.fieldId !== 'status');
      if (!value) return rest;
      return [...rest, { id: `f-status-${Date.now()}`, fieldId: 'status', operator: 'is', value }];
    });
  }, []);

  const clearFinancesFilters = React.useCallback(() => setActiveFilters([]), []);

  // Handle URL parameter for payment selection (scroll + highlight)
  useTableRowNavigation<Payment, 'sent' | 'paid' | 'overdue' | ''>({
    paramName: 'paymentId',
    dataAttribute: 'data-payment-id',
    allItems: payments,
    getId: (payment) => payment.id,
    getFilterValue: (payment) => payment.status as 'sent' | 'paid' | 'overdue',
    currentFilter: statusFilter,
    setFilter: setStatusFilterFromNav,
    fetchItem: (id) => paymentsService.getById(id),
    clearFilters: clearFinancesFilters,
  });

  // Load chart payments for revenue window (avoid paymentsService.getAll on mount)
  const chartLoadSeqRef = useRef(0);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const seq = ++chartLoadSeqRef.current;
      try {
        const now = new Date();
        // Same visible window as generateMonthsData: -11 months .. +3 months
        const start = new Date(now.getFullYear(), now.getMonth() - 11, 1, 0, 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth() + 4, 1, 0, 0, 0, 0);
        const d = await paymentsService.getForRevenueWindow(start, end);
        if (cancelled) return;
        if (seq !== chartLoadSeqRef.current) return;
        setChartPayments(d);
        setChartPaymentsLoaded(true);
      } catch (e) {
        console.error('Error loading chart payments:', e);
        if (!cancelled) setChartPaymentsLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Load projects/leads only for currently known payments (avoid projectsService.getAll/leadsService.getAll)
  const projectsLoadSeqRef = useRef(0);
  const projectsKnownIdsRef = useRef<Set<string>>(new Set());
  const leadsLoadSeqRef = useRef(0);
  const leadsKnownIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    const projectIds = Array.from(new Set([...payments, ...chartPayments].map((p) => p.projectId).filter(Boolean)));
    const missing = projectIds.filter((id) => !projectsKnownIdsRef.current.has(id));
    if (missing.length === 0) return;

    const load = async () => {
      const seq = ++projectsLoadSeqRef.current;
      try {
        const chunkSize = 200;
        const chunks: string[][] = [];
        for (let i = 0; i < missing.length; i += chunkSize) chunks.push(missing.slice(i, i + chunkSize));

        const results: Project[] = [];
        for (const chunk of chunks) {
          const part = await projectsService.getByIds(chunk);
          results.push(...part);
        }

        if (cancelled) return;
        if (seq !== projectsLoadSeqRef.current) return;
        missing.forEach((id) => projectsKnownIdsRef.current.add(id));
        setProjects((prev) => {
          const map = new Map<string, Project>();
          prev.forEach((p) => map.set(p.id, p));
          results.forEach((p) => map.set(p.id, p));
          return Array.from(map.values());
        });
      } catch (e) {
        console.error('Error loading projects for payments:', e);
        missing.forEach((id) => projectsKnownIdsRef.current.add(id));
      }
    };

    load();
    return () => { cancelled = true; };
  }, [payments, chartPayments]);

  useEffect(() => {
    let cancelled = false;
    const leadIds = Array.from(new Set(projects.map((p) => p.leadId).filter(Boolean)));
    const missing = leadIds.filter((id) => !leadsKnownIdsRef.current.has(id));
    if (missing.length === 0) return;

    const load = async () => {
      const seq = ++leadsLoadSeqRef.current;
      try {
        const chunkSize = 200;
        const chunks: string[][] = [];
        for (let i = 0; i < missing.length; i += chunkSize) chunks.push(missing.slice(i, i + chunkSize));
        const results: any[] = [];
        for (const chunk of chunks) {
          const part = await leadsService.getByIds(chunk);
          results.push(...part);
        }
        if (cancelled) return;
        if (seq !== leadsLoadSeqRef.current) return;
        missing.forEach((id) => leadsKnownIdsRef.current.add(id));
        setLeads((prev) => {
          const map = new Map<string, any>();
          prev.forEach((l) => map.set(l.id, l));
          results.forEach((l) => map.set(l.id, l));
          return Array.from(map.values());
        });
      } catch (e) {
        console.error('Error loading leads for payments:', e);
        missing.forEach((id) => leadsKnownIdsRef.current.add(id));
      }
    };

    load();
    return () => { cancelled = true; };
  }, [projects]);

  // Listen for payment due status sync (from Layout) to refresh data
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ toOverdueIds: string[]; toSentIds: string[] }>).detail;
      if (!detail) return;
      const { toOverdueIds = [], toSentIds = [] } = detail;
      const overdueIds = new Set(toOverdueIds);
      const revertIds = new Set(toSentIds);
      setChartPayments((prev) =>
        prev.map((p) => {
          if (overdueIds.has(p.id)) return { ...p, status: 'overdue' as any };
          if (revertIds.has(p.id)) return { ...p, status: 'sent' as any };
          return p;
        })
      );
      refresh();
    };
    window.addEventListener(PAYMENTS_DUE_STATUS_UPDATED, handler);
    return () => window.removeEventListener(PAYMENTS_DUE_STATUS_UPDATED, handler);
  }, [refresh]);

  // Keep chart payments cache in sync with optimistic list updates
  useEffect(() => {
    if (!chartPaymentsLoaded) return;

    setChartPayments((prev) => {
      const tempIdsInList = new Set(payments.filter((p) => p.id.startsWith('temp-')).map((p) => p.id));
      const map = new Map<string, Payment>();

      // Keep existing non-temp items + keep temp items only if still present in list
      prev.forEach((p) => {
        if (p.id.startsWith('temp-') && !tempIdsInList.has(p.id)) return;
        map.set(p.id, p);
      });

      // Upsert current list items (includes temp optimistic items)
      payments.forEach((p) => map.set(p.id, p));

      return Array.from(map.values());
    });
  }, [payments, chartPaymentsLoaded]);

  const [taxRate, setTaxRate] = useState<number>(0.25); // decimal

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await profilesService.getProfile();
        if (profile && typeof profile.tax_rate === 'number') {
          setTaxRate(profile.tax_rate);
        }
        if (profile) {
          setIssuerProfile(sellerFromProfile(profile));
        }
      } catch (e) {
        // keep default if error
      }
    };
    loadProfile();
  }, []);

  // Load invoices linked to currently listed payments
  useEffect(() => {
    if (!ENABLE_INVOICES) {
      setInvoicesByPaymentId({});
      return;
    }
    let cancelled = false;
    const ids = payments.map((p) => p.id).filter((id) => !id.startsWith('temp-'));
    if (ids.length === 0) {
      setInvoicesByPaymentId({});
      return;
    }
    invoicesService.getByPaymentIds(ids).then((rows) => {
      if (cancelled) return;
      const map: Record<string, Invoice> = {};
      rows.forEach((inv) => {
        map[inv.paymentId] = inv;
      });
      setInvoicesByPaymentId(map);
    });
    return () => {
      cancelled = true;
    };
  }, [payments]);



  // Ordre = colonnes de la table (gauche → droite) : Projet, Date émission, Échéance, Date paiement, Statut, Montant ; puis Période (filtre spécial)
  const paymentFilterFields: FilterFieldDef[] = useMemo(
    () => [
      { id: 'project', type: 'text', label: t('payments.project'), placeholder: t('common.search') },
      { id: 'issueDate', type: 'date', label: t('payments.issueDate'), placeholder: '' },
      { id: 'dueDate', type: 'date', label: t('payments.dueDate'), placeholder: '' },
      { id: 'paymentDate', type: 'date', label: t('payments.paymentDate'), placeholder: '' },
      {
        id: 'status',
        type: 'select',
        label: t('payments.statusLabel'),
        options: [
          { value: '', label: t('common.allStatus') },
          { value: 'sent', label: t('payments.statusLabels.sent') },
          { value: 'paid', label: t('payments.statusLabels.paid') },
          { value: 'overdue', label: t('payments.statusLabels.overdue') },
        ],
      },
      { id: 'amount', type: 'number', label: t('payments.amount'), placeholder: '0' },
      {
        id: 'period',
        type: 'select',
        label: t('common.period'),
        options: [
          { value: '', label: t('common.allPeriods') },
          { value: 'month', label: t('common.thisMonth') },
          { value: 'year', label: t('common.thisYear') },
        ],
      },
    ],
    [t]
  );

  // Filter payments based on search + all active filters
  const filteredPayments = useMemo(() => {
    const term = deferredSearchTerm.trim().toLowerCase();
    return payments.filter((payment) => {
      const project = projects.find((p) => p.id === payment.projectId);
      const projectName = (project?.name ?? '').toLowerCase();
      const lead = leads.find((l: { id: string }) => l.id === project?.leadId);
      const leadName = (lead?.name ?? '').toLowerCase();
      const matchesSearch = !term || projectName.includes(term) || leadName.includes(term);
      return matchesSearch && paymentMatchesActiveFilters(payment, project, lead, activeFilters, paymentFilterFields);
    });
  }, [payments, projects, leads, deferredSearchTerm, activeFilters, paymentFilterFields]);

  const showTableInitialLoading = useTableInitialLoading(loading, payments.length);

  const {
    tableProps: bulkTableProps,
    bulkBarProps,
    bulkDeleteModal,
    selectedItems: selectedPayments,
  } = useBulkTableActions<Payment>({
    items: filteredPayments,
    onBulkDeleteConfirm: (ids) => {
      paymentsCRUD.bulkRemove(ids);
      setChartPayments((prev) => prev.filter((p) => !ids.includes(p.id)));
    },
  });

  const handleBulkStatusUpdate = (ids: string[], newStatus: string) => {
    const selectedById = new Map(selectedPayments.map((p) => [p.id, p]));
    const basePayments = ids
      .map((id) => selectedById.get(id) ?? payments.find((p) => p.id === id))
      .filter((payment): payment is Payment => payment !== undefined);

    if (basePayments.length !== ids.length) {
      showToast(t('payments.selectedNoLongerAvailable'), 'warning');
    }

    const updatedPayments = basePayments.map((payment) => ({
      ...payment,
      status: newStatus as 'sent' | 'paid' | 'overdue',
      ...(newStatus === 'paid' && { paymentDate: new Date() }),
    }));

    paymentsCRUD.bulkUpdate(updatedPayments);
    setChartPayments((prev) => {
      const byId = new Map(updatedPayments.map((p) => [p.id, p]));
      return prev.map((p) => byId.get(p.id) ?? p);
    });
    notifyPaymentMutated();
  };

  const handleDeletePayment = (id: string) => {
    const paymentToDelete = payments.find(inv => inv.id === id);
    if (!paymentToDelete) return;

    // Store the payment data for undo
    const paymentData = { ...paymentToDelete };
    
    // Delete with optimistic UI (instant feedback)
    paymentsCRUD.remove(id);
    setChartPayments((prev) => prev.filter((p) => p.id !== id));

    // Show toast with undo immediately
    showToastWithUndo(
      t('payments.deleted', { number: paymentToDelete.id }),
      async () => {
        // Undo: recreate the payment
        const result = await paymentsService.create({
          projectId: paymentData.projectId,
          amount: paymentData.amount,
          currency: paymentData.currency || 'EUR',
          amountBaseCurrency: paymentData.amountBaseCurrency || (paymentData.currency === 'EUR' ? paymentData.amount : 0),
          status: paymentData.status,
          dueDate: paymentData.dueDate,
          issueDate: paymentData.issueDate,
          paymentDate: paymentData.paymentDate,
        });
        if (result) {
          addPaginatedPayment(result as Payment);
        }
      },
      'error'
    );
  };

  const handleMarkAsPaid = (payment: Payment) => {
    setMarkingAsPaidPayment(payment);
    setIsMarkAsPaidModalOpen(true);
  };

  const handleConfirmMarkAsPaid = (paymentDate: Date, amountBaseCurrency: number) => {
    if (!markingAsPaidPayment) return;

    const updated: Payment = {
      ...markingAsPaidPayment,
      status: 'paid',
      paymentDate,
      amountBaseCurrency: revenueAmountForMarkAsPaid(markingAsPaidPayment, amountBaseCurrency),
      updatedAt: new Date(),
    };

    setMonthlyRevenueFromAPI(null);
    paymentsCRUD.update(markingAsPaidPayment.id, updated, { previousItem: markingAsPaidPayment });
    setChartPayments((prev) => {
      const exists = prev.some((p) => p.id === updated.id);
      if (!exists) return [...prev, updated];
      return prev.map((p) => (p.id === updated.id ? updated : p));
    });
    notifyPaymentMutated();
  };

  const handleCreatePayment = () => {
    setEditingPayment(null);
    setPreselectedProject(null);
    setIsPaymentModalOpen(true);
  };


  const handleEditPayment = (payment: Payment) => {
    setEditingPayment(payment);
    setIsPaymentModalOpen(true);
  };

  const handleAddInvoice = useCallback((payment: Payment) => {
    if (payment.id.startsWith('temp-')) {
      showToast(t('common.saveFailedRollback'), 'warning');
      return;
    }
    navigate(`/app/finances/invoices/new?paymentId=${encodeURIComponent(payment.id)}`);
  }, [navigate, showToast, t]);

  const handleOpenInvoicePdf = useCallback((payment: Payment, invoice: Invoice) => {
    const project = projects.find((p) => p.id === payment.projectId);
    downloadPaymentInvoicePdf({
      payment,
      invoice,
      seller: issuerProfile,
      projectName: project?.name,
      formatDateShort,
      formatCurrency,
      t,
    });
  }, [projects, issuerProfile, formatDateShort, formatCurrency, t]);

  const handleClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setEditingPayment(null);
    setPreselectedProject(null);
  };

  const handleSavePayment = (paymentData: Omit<Payment, 'id' | 'createdAt' | 'updatedAt'>) => {
    // Auto-correct status based on due date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(paymentData.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    
    let correctedStatus = paymentData.status;
    if (paymentData.status === 'sent' && dueDate < today) {
      correctedStatus = 'overdue';
    } else if (paymentData.status === 'overdue' && dueDate >= today) {
      correctedStatus = 'sent';
    }
    
    const correctedData = { ...paymentData, status: correctedStatus };
    
    if (editingPayment) {
      const updated: Payment = { ...editingPayment, ...correctedData, updatedAt: new Date() };
      paymentsCRUD.update(editingPayment.id, updated, { previousItem: editingPayment });
      setChartPayments((prev) => {
        const exists = prev.some((p) => p.id === updated.id);
        if (!exists) return [...prev, updated];
        return prev.map((p) => (p.id === updated.id ? updated : p));
      });
    } else {
      paymentsCRUD.create(correctedData);
    }
    notifyPaymentMutated();

    // Close modal
    handleClosePaymentModal();
  };


  const handleExport = async () => {
    try {
      // Fetch ALL payments directly from service to ensure we get everything
      const allPayments = await paymentsService.getAll();

      if (allPayments.length === 0) {
        showToast(t('payments.noPaymentsToExport'), 'warning');
        return;
      }

      // Apply same filters as table (search + all active filters with all operators)
      const searchLower = searchTerm.trim().toLowerCase();
      const filteredPayments = allPayments.filter((payment: Payment) => {
        const project = projects.find((p) => p.id === payment.projectId);
        const lead = leads.find((l: { id: string }) => l.id === project?.leadId);
        if (searchLower) {
          const projectNameMatch = (project?.name ?? '').toLowerCase().includes(searchLower);
          const leadNameMatch = (lead?.name ?? '').toLowerCase().includes(searchLower);
          if (!projectNameMatch && !leadNameMatch) return false;
        }
        return paymentMatchesActiveFilters(payment, project, lead, activeFilters, paymentFilterFields);
      });

      if (filteredPayments.length === 0) {
        showToast(t('payments.noPaymentsToExport'), 'warning');
        return;
      }

      const exportHeaders = [
        t('payments.project'),
        t('payments.amount'),
        t('payments.status'),
        t('payments.issueDate'),
        t('payments.dueDate'),
        t('payments.paymentDate'),
        t('leads.createdAt')
      ];

      const exportData = filteredPayments.map((payment: Payment) => {
        const project = projects.find(p => p.id === payment.projectId);
        return {
          [t('payments.project')]: project?.name || t('projects.unknownLead'),
          [t('payments.amount')]: payment.amount ?? 0,
          [t('payments.status')]: payment.status,
          [t('payments.issueDate')]: payment.issueDate != null ? formatDateTimeForExport(payment.issueDate) : '',
          [t('payments.dueDate')]: payment.dueDate != null ? formatDateTimeForExport(payment.dueDate) : '',
          [t('payments.paymentDate')]: payment.paymentDate != null ? formatDateTimeForExport(payment.paymentDate) : '',
          [t('leads.createdAt')]: payment.createdAt != null ? formatDateTimeForExport(payment.createdAt) : ''
        };
      });

      exportToCSV(exportData, `finances-${new Date().toISOString().split('T')[0]}`, exportHeaders);
      showToast(t('common.exportSuccess', { count: exportData.length }), 'success');
    } catch (error) {
      console.error('Error exporting finances:', error);
      showToast(t('finances.exportError'), 'error');
    }
  };



  return (
    <div className={`w-full ${PAGE_STACK}`}>
      {/* Header */}
      <PageHeader
        title={t('finances.title')}
        description={t('finances.manageFinances')}
        actionsConfig={{
          newButtonKey: 'payments.newPayment',
          onCreateNew: () => handleCreatePayment(),
          onExport: handleExport,
          hideNewOnMobile: true,
        }}
      />

      {/* Stats removed on non-overview pages */}

      {/* Chart 2/3 + fiscal summary & upcoming 1/3 (same row height) */}
      <div className={`grid grid-cols-1 lg:grid-cols-3 ${PAGE_GRID_GAP} lg:items-stretch`}>
        <div className="relative flex min-h-0 flex-col lg:col-span-2">
          <div
            className={`flex min-h-0 flex-1 flex-col transition-opacity duration-300 ${
              revenueLoading || !chartPaymentsLoaded ? 'opacity-50' : 'opacity-100'
            }`}
          >
            <PaymentRevenueChart data={revenueData} className="h-full" fillHeight />
          </div>
        </div>

        <div className={`flex min-h-0 flex-col ${PAGE_STACK}`}>
          <Card fitContent className="shrink-0">
            <CardHeader>
              <div className="flex items-center justify-between gap-2 min-w-0">
                <CardTitle className="flex items-center gap-2 min-w-0">
                  <Euro className="w-5 h-5 flex-shrink-0 text-green-600" />
                  <span className="sm:hidden truncate">{t('finances.summaryShort')}</span>
                  <span className="hidden sm:inline truncate">{t('finances.monthlyFinancialSummary')}</span>
                </CardTitle>
                <SegmentedToggle
                  value={summaryMonth}
                  onChange={(v) => setSummaryMonth(v as 'this' | 'last')}
                  options={[
                    { key: 'this', label: t('common.thisMonth') },
                    { key: 'last', label: t('common.lastMonth') },
                  ]}
                  size="sm"
                  className="shrink-0"
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center min-h-[28px]">
                  <span className="text-sm text-gray-600">{t('finances.grossRevenue')}</span>
                  <span
                    className={`font-semibold text-green-600 tabular-nums ${
                      summaryRevenueLoading ? 'opacity-60' : ''
                    }`}
                  >
                    {formatCurrency(monthlyRevenue)}
                  </span>
                </div>

                <div className="flex justify-between items-center min-h-[28px] gap-2">
                  <span className="text-sm text-gray-600">
                    {t('finances.estimatedTaxes')}{' '}
                    <Tooltip content={t('finances.editTaxRate')}>
                      <button
                        type="button"
                        onClick={() => navigate('/app/settings?tab=profile&focus=taxRate')}
                        className={`${clickableLinks} font-medium hover:underline`}
                      >
                        ({Math.round(taxRate * 100)}&nbsp;%)
                      </button>
                    </Tooltip>
                  </span>
                  <span
                    className={`font-medium text-red-600 tabular-nums ${
                      summaryRevenueLoading ? 'opacity-60' : ''
                    }`}
                  >
                    -{formatCurrency(monthlyRevenue * taxRate)}
                  </span>
                </div>

                <div className="border-t pt-3">
                  <div className="flex justify-between items-center min-h-[32px]">
                    <span className="font-semibold text-gray-900">{t('finances.netRevenue')}</span>
                    <span
                      className={`font-bold text-lg text-green-600 tabular-nums ${
                        summaryRevenueLoading ? 'opacity-60' : ''
                      }`}
                    >
                      {formatCurrency(monthlyRevenue * (1 - taxRate))}
                    </span>
                  </div>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 leading-relaxed">
                    {t('finances.taxDisclaimer')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="flex min-h-0 flex-1 flex-col">
            <CardHeader>
              <div className="flex items-center justify-between gap-2 min-h-7">
                <CardTitle className="flex items-center space-x-2 min-w-0">
                  <Calendar className="w-5 h-5 shrink-0 text-amber-500" />
                  <span className="truncate">{t('finances.upcomingPayments')}</span>
                </CardTitle>
                {showUpcomingNav && (
                  <SlidingWindowNav
                    canPrev={canPrevUpcoming}
                    canNext={canNextUpcoming}
                    onPrev={prevUpcoming}
                    onNext={nextUpcoming}
                    tone="neutral"
                  />
                )}
              </div>
            </CardHeader>
            <CardContent className="flex-1">
              {loading ? (
                <Skeleton className="h-full min-h-[8rem] w-full" />
              ) : upcomingPayments.length === 0 ? (
                <div className="flex h-full min-h-[8rem] flex-col items-center justify-center py-6 text-center">
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">{t('finances.allCaughtUp')}</p>
                  <p className="mt-1 text-xs text-gray-500">{t('finances.noUpcomingPayments')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {visibleUpcomingPayments.map((payment) => {
                    const project = projects.find((p) => p.id === payment.projectId);
                    const isOverdue = payment.status === 'overdue';

                    return (
                      <div
                        key={payment.id}
                        className={`box-border flex h-[3.25rem] min-h-[3.25rem] max-h-[3.25rem] flex-col justify-center rounded-lg border px-2 py-1 ${
                          isOverdue
                            ? 'border-red-200 bg-red-50'
                            : 'border-yellow-200 bg-yellow-50'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium leading-tight text-gray-900">
                            {project?.name || t('finances.deletedProject')}
                          </span>
                          <span className="shrink-0 text-sm font-semibold leading-tight text-gray-900 tabular-nums">
                            {formatCurrency(payment.amount, payment.currency || 'EUR')}
                          </span>
                        </div>
                        <div className="mt-0.5 flex items-center justify-between gap-2">
                          <span className="text-[11px] leading-tight text-gray-600">
                            {formatDateLocale(payment.dueDate)}
                          </span>
                          <span
                            className={`text-[11px] font-medium leading-tight ${
                              isOverdue ? 'text-red-600' : 'text-yellow-600'
                            }`}
                          >
                            {isOverdue ? t('finances.overdue') : t('finances.pending')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Payments List */}
      <TableSection
        actionsConfig={{}}
        filters={
          <TableFilters
            searchConfig={{
              value: searchTerm,
              onChange: setSearchTerm,
              placeholder: t('payments.searchByProject'),
            }}
            filterFields={paymentFilterFields}
            activeFilters={activeFilters}
            onChange={setActiveFilters}
            mobilePrimaryAction={
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleCreatePayment()}
                icon={<Plus className="w-4 h-4" />}
              >
                {t('common.new')}
              </Button>
            }
          />
        }
      >
        {/* Bulk actions bar - selection count, change status, delete */}
        {!showTableInitialLoading && !error && (
          <BulkActionsBar
            {...bulkBarProps}
            statusOptions={paymentStatusOptions}
            onUpdateStatus={handleBulkStatusUpdate}
          />
        )}

        <Card padding="none">
          <CardContent className="p-0">
            <TableCardContent error={error} showInitialLoading={showTableInitialLoading} onRetry={refresh}>
              <PaymentTable
                payments={filteredPayments}
                projects={projects}
                invoicesByPaymentId={ENABLE_INVOICES ? invoicesByPaymentId : undefined}
                onEdit={handleEditPayment}
                onDelete={handleDeletePayment}
                onMarkAsPaid={handleMarkAsPaid}
                onAddInvoice={ENABLE_INVOICES ? handleAddInvoice : undefined}
                onOpenInvoice={ENABLE_INVOICES ? handleOpenInvoicePdf : undefined}
                onBulkDelete={bulkTableProps.onBulkDelete}
                onRegisterToggleAll={bulkTableProps.onRegisterToggleAll}
                onRegisterBulkDelete={bulkTableProps.onRegisterBulkDelete}
                onRegisterClearSelection={bulkTableProps.onRegisterClearSelection}
                onSelectedCountChange={bulkTableProps.onSelectedCountChange}
                onIsAllSelectedChange={bulkTableProps.onIsAllSelectedChange}
                onSelectedPaymentsChange={bulkTableProps.onSelectedItemsChange as (payments: Payment[]) => void}
                hideBulkActionsBar
                hasMore={hasMore}
                loadingMore={loadingMore}
                onLoadMore={loadMore}
                isFilteredEmpty={activeFilters.length > 0 || deferredSearchTerm.trim().length > 0}
                onCreate={handleCreatePayment}
              />
            </TableCardContent>
          </CardContent>
        </Card>
      </TableSection>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={handleClosePaymentModal}
        onSave={handleSavePayment}
        payment={editingPayment}
        projects={projects}
        leads={leads}
        searchProjectsFn={(term) => projectsService.search(term, 10)}
        preselectedProject={preselectedProject}
        title={editingPayment ? t('payments.editPayment') : t('payments.createPayment')}
        onCreateProject={handleCreateProjectFromSearch}
      />

      {leadCreateModal}
      {projectCreateModal}
      {/* Mark as Paid Modal */}
      <MarkAsPaidModal
        isOpen={isMarkAsPaidModalOpen}
        onClose={() => setIsMarkAsPaidModalOpen(false)}
        onConfirm={handleConfirmMarkAsPaid}
        payment={markingAsPaidPayment}
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={bulkDeleteModal.isOpen}
        onClose={bulkDeleteModal.onClose}
        onConfirm={bulkDeleteModal.onConfirm}
        title={t('common.confirm')}
        message={t('payments.confirmBulkDelete', { count: bulkDeleteModal.pendingCount })}
        variant="danger"
      />

    </div>
  );
};

export default Finances;